// Pure types + helpers for the org hierarchy tree. No data lives here —
// the live tree is built from the get_hierarchy_tree RPC in hierarchyLive.ts.

export type OrgNodeType = 'platform' | 'imo' | 'agency' | 'team' | 'agent';

export interface ClientRef {
  id: string;
  name: string;
  lastEdit: string;
}

export interface OrgNode {
  id: string;
  type: OrgNodeType;
  name: string;
  admin: string;
  /** Agent leaves carry the person's email for reset-password; orgs don't. */
  email?: string;
  flags: { alts: boolean; fia: boolean };
  children: OrgNode[];
  clientCount: number;
  clients: ClientRef[];
  status?: 'active' | 'suspended' | 'pending' | 'archived';
  /** Agent leaves: false = a support admin with no client book of their own
   *  (profiles.has_client_book). Undefined before 20260918000001 = has a book. */
  hasClientBook?: boolean;
  /** The member's role in their own org ('agent' | 'org_admin' | 'org_owner').
   *  Every member leaf renders as type 'agent'; this is what they actually are. */
  orgRole?: string;
  /** Org nodes: owned by platform staff (the company under the root,
   *  20260930000002). Deactivate is the super admin's alone on such a row. */
  staffOwned?: boolean;
  /** Org nodes: the org's admins (org_admin role), clients or not. The tree
   *  only renders people who carry clients, so without this list an admin
   *  with no book had no tile — and no Suspend or Delete anywhere. */
  admins?: OrgNode[];
}

export interface AuditEntry {
  actor: string;
  verb: string;
  target: string;
  detail: string;
  time: string;
  kind: 'role' | 'flag' | 'invite' | 'sub' | 'dup' | 'add' | 'status';
}

export type FlagKey = 'alts' | 'fia';

export const FLAG_META: { key: FlagKey; label: string; help: string }[] = [
  { key: 'alts', label: 'Alts', help: 'Alternative investment strategies' },
  { key: 'fia', label: 'FIA', help: 'Fixed indexed annuity sequencing' },
];

export function findNode(root: OrgNode, id: string): OrgNode | null {
  if (root.id === id) return root;
  for (const c of root.children) {
    const r = findNode(c, id);
    if (r) return r;
  }
  return null;
}

export function findPath(root: OrgNode, id: string, acc: OrgNode[] = []): OrgNode[] | null {
  const next = [...acc, root];
  if (root.id === id) return next;
  for (const c of root.children) {
    const r = findPath(c, id, next);
    if (r) return r;
  }
  return null;
}

export function parentOf(root: OrgNode, id: string): OrgNode | null {
  for (const c of root.children) {
    if (c.id === id) return root;
    const r = parentOf(c, id);
    if (r) return r;
  }
  return null;
}

export interface Rollup {
  imo: number;
  agency: number;
  team: number;
  agents: number;
  clients: number;
}

export function rollup(node: OrgNode): Rollup {
  const r: Rollup = { imo: 0, agency: 0, team: 0, agents: 0, clients: 0 };
  const walk = (n: OrgNode) => {
    for (const c of n.children) {
      if (c.type === 'imo') r.imo++;
      else if (c.type === 'agency') r.agency++;
      else if (c.type === 'team') r.team++;
      else if (c.type === 'agent') { r.agents++; r.clients += c.clientCount; }
      walk(c);
    }
  };
  walk(node);
  return r;
}

export function rollupLine(node: OrgNode): string {
  if (node.type === 'agent') return node.clientCount + ' clients';
  const r = rollup(node);
  const parts: string[] = [];
  if (r.imo) parts.push(r.imo + (r.imo === 1 ? ' IMO' : ' IMOs'));
  if (r.agency) parts.push(r.agency + (r.agency === 1 ? ' agency' : ' agencies'));
  if (r.team) parts.push(r.team + (r.team === 1 ? ' team' : ' teams'));
  parts.push(r.agents + (r.agents === 1 ? ' agent' : ' agents'));
  parts.push(r.clients.toLocaleString() + ' clients');
  return parts.join('  ·  ');
}

export function typeLabel(t: OrgNodeType): string {
  return { platform: 'Platform', imo: 'IMO', agency: 'Agency', team: 'Team', agent: 'Agent' }[t];
}

export function initials(name: string): string {
  return name.split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase() || '?';
}

export function flattenDownline(node: OrgNode): OrgNode[] {
  const result: OrgNode[] = [];
  const walk = (n: OrgNode) => {
    for (const c of n.children) {
      result.push(c);
      walk(c);
    }
  };
  walk(node);
  return result;
}

/**
 * Effective flag at a node: DEEPEST explicit setting on the path root→node
 * wins; nothing explicit anywhere = ON. `overrides` is keyed `${nodeId}__${flag}`
 * and holds the explicit per-node settings (from org_feature_flags plus any
 * optimistic local writes).
 */
export function hasFlag(
  root: OrgNode,
  node: OrgNode,
  flag: FlagKey,
  overrides: Record<string, boolean>,
): boolean {
  const path = findPath(root, node.id);
  if (!path) return false;
  let state = true;
  for (const n of path) {
    const k = n.id + '__' + flag;
    if (k in overrides) {
      state = overrides[k];
    } else if (n.flags[flag] === false) {
      state = false;
    }
  }
  return state;
}

export interface ClientSearchHit {
  client: ClientRef;
  /** The node (agent or advisor) whose client list carries this client. */
  agent: OrgNode;
}

/** Every client at or below `node`, each paired with its owning node. */
function collectClients(node: OrgNode): ClientSearchHit[] {
  const hits: ClientSearchHit[] = [];
  const walk = (n: OrgNode) => {
    for (const client of n.clients) hits.push({ client, agent: n });
    for (const child of n.children) walk(child);
  };
  walk(node);
  return hits;
}

/**
 * Case-insensitive substring search over every client at or below `node`.
 * Backs the Team Management client search: the tree only renders clients on
 * agent leaves, so without this you'd have to know which agent holds a client
 * before you could find it. A blank query returns nothing.
 */
/** Every client at or below `node`: the Clients level filter with no search text. */
export function allClients(node: OrgNode): ClientSearchHit[] {
  return collectClients(node);
}

export function searchClients(node: OrgNode, query: string): ClientSearchHit[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  return collectClients(node).filter(h => h.client.name.toLowerCase().includes(q));
}

export interface FlagSource {
  /** Effective state at the node — always equals hasFlag(). */
  on: boolean;
  /** True when this node carries its own explicit setting. */
  setHere: boolean;
  /** The nearest node above it with an explicit setting — where the value is
   *  inherited from. Null when nothing above sets it (the default: on). */
  from: OrgNode | null;
}

/**
 * Where a node's flag value comes from. Same walk as hasFlag(), but it also
 * reports whether the node set the value itself or inherits it, and from whom,
 * so the switch can say so instead of showing a bare On/Off.
 */
export function flagSource(
  root: OrgNode,
  node: OrgNode,
  flag: FlagKey,
  overrides: Record<string, boolean>,
): FlagSource {
  const path = findPath(root, node.id);
  if (!path) return { on: false, setHere: false, from: null };
  let on = true;
  let from: OrgNode | null = null;
  let setHere = false;
  for (const n of path) {
    const k = n.id + '__' + flag;
    const explicit = k in overrides ? overrides[k] : n.flags[flag] === false ? false : undefined;
    if (explicit === undefined) continue;
    on = explicit;
    if (n.id === node.id) setHere = true;
    else from = n;
  }
  return { on, setHere, from: setHere ? null : from };
}

/** Ids on the path from the root down to (not including) `id` — the branches
 *  that must be open for that node to be visible in the tree. */
export function ancestorIds(root: OrgNode, id: string): string[] {
  const path = findPath(root, id);
  return path ? path.slice(0, -1).map(n => n.id) : [];
}

/**
 * People at or below a node, each counted once. Member leaves (agents, and
 * owners/admins who carry clients) plus every org's admins — an admin with no
 * client book has no leaf, and an admin who carries clients is on both lists,
 * so the ids are deduplicated.
 */
export function memberCount(node: OrgNode): number {
  const ids = new Set<string>();
  const walk = (n: OrgNode) => {
    if (n.type === 'agent') { ids.add(n.id); return; }
    for (const a of n.admins ?? []) ids.add(a.id);
    n.children.forEach(walk);
  };
  walk(node);
  return ids.size;
}
